import { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { TabContent, Collapse, Card, CardBody, Badge } from 'reactstrap'
// import { ExternalLink } from 'lucide-react'
import Loader from '../../../../../Material/Loader'
import './ExternalIntegrations.css'
import InteractionContainer from '../../../../Interactions/InteractionsContainer'
import hubspotIcon from "../../../../../assets/images/icons/hubspot-logo.png"
import DriverContextPanel from './DriverContextPanel'
import { ALL_CONVERSATIONS } from '../../../../../Communication/Queries'
import CommsConversation from '../../../../../Communication/CommsConversation'
import CommsNewConversationModal from '../../../../../Communication/CommsNewConversationModal'
import { HasPermissionsQuery } from '../../../../../Functions/querys'
import DriverTasksTab from '../TasksTab/DriverTasksTab'

const HUBSPOT_INTEGRATION_DATA = gql`
  query hubspotIntegrationData($driverId: ID!) {
    hubspotIntegrationData(driverId: $driverId) {
      hubspotContactUrl
      newTicketsCount
      tickets {
        id
        title
        status
        description
        dateCreated
      }
    }
  }
`
const TABS = [
  { id: 'tasks', name: 'TASKS', logoComponent: <i className="fa fa-columns"></i> },
  { id: 'interactions', name: 'INTERACTIONS', logoComponent: <i className="fa fa-comments"></i> },
  { id: 'driverContext', name: 'DRIVER CONTEXT', logoComponent: <i className="fa fa-id-card-o"></i> },
  { id: 'conversations', name: 'CONVERSATIONS', requiredPermission: 'view_conversation', logoComponent: <i className="fa fa-envelope"></i> },
]

const ConversationsTab = ({ driverId }) => {
  const [openConversation, setOpenConversation] = useState('')
  const [isNewConversationModalOpen, setIsNewConversationModalOpen] = useState(false)
  const { data, loading, refetch } = useQuery(ALL_CONVERSATIONS, { variables: { driver: driverId, last: 10 }, skip: !driverId, fetchPolicy: 'cache-and-network'})

  const handleConversationCreated = (newConversationId) => {
    setIsNewConversationModalOpen(false)
    setOpenConversation(newConversationId)
    refetch()
  }

  if (loading) return <Loader />

  const conversations = data?.allConversations?.edges || []

  return (
    <div className="conversations-tab">
      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="driver-task-create-button"
          onClick={() => setIsNewConversationModalOpen(true)}
        >
          <i className="fa fa-plus-square" aria-hidden="true" />
          New Conversation
        </button>
      </div>
      {conversations.length === 0 ? (
        <p>No conversations found for this driver.</p>
      ) : (
        <ul className="collapse-ul">
          {conversations.map(({ node: conversation }) => {
            const subject = conversation.subject || conversation.contactName || 'Conversation';
            const inboxName = conversation?.channel?.inbox?.name || 'Inbox';
            return (
              <li key={conversation.id} className="collapse-li">
                <button
                  className={`collapse-button`}
                  type="button"
                  onClick={() => setOpenConversation(openConversation === conversation.id ? '' : conversation.id)}
                >
                  <span className="collapse-button-label">
                    {subject} - {inboxName}
                  </span>
                  <span className="collapse-button-meta">
                    <i
                      className={`collapse-arrow fa fa-chevron-down`}
                      aria-hidden="true"
                    />
                  </span>
                </button>
                <Collapse isOpen={openConversation === conversation.id}>
                  <CommsConversation conversationIdFromParent={conversation.id} slimView={true} />
                </Collapse>
              </li>
            )
          })}
        </ul>
      )}
      {isNewConversationModalOpen && (
        <CommsNewConversationModal
          isOpen={isNewConversationModalOpen}
          toggle={() => setIsNewConversationModalOpen(false)}
          defaultDriverId={driverId}
          onCreated={handleConversationCreated}
        />
      )}
    </div>
    )
}



const ExternalIntegrations = ({ driverId }) => {
  const [tab, setTab] = useState('hubspot')
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const {  data: permissionsData } = useQuery(HasPermissionsQuery, {
        variables: {
            userPermissions: ["view_conversation", "view_taskrabbit"],
        },
    });
  const userPermissions = permissionsData?.hasPermissions || [];
  const togglePanel = (tabName) => {
    if (tab === tabName && isPanelOpen) {
      setIsPanelOpen(false)
    } else {
      setTab(tabName)
      setIsPanelOpen(true)
    }
  }

  const currentTabName = useMemo(() => TABS.find(item => item.id === tab)?.name || '', [tab])

  if (!driverId) return null

  return (
    <div className="external-integrations">
      {/* Floating side icons */}
      <div className="integration-icon-container">
        {TABS.map(({ id, name, logoComponent, requiredPermission }) => {
          if (requiredPermission && !userPermissions.includes(requiredPermission)) {
            return null;
          }
          return (
            <div
              key={id}
              className={`integration-icon ${tab === id && isPanelOpen ? 'active' : ''}`}
              onClick={() => togglePanel(id)}
              title={name}
            >
              {logoComponent}
            </div>
          )
        })}
      </div>

      {/* Slide panel */}
      <div className={`integration-panel ${isPanelOpen ? 'open' : ''}`}>
        <div className="integration-panel-header">
          <h5>{currentTabName}</h5>
          <button className="close-btn" onClick={() => setIsPanelOpen(false)}>
            ✕
          </button>
        </div>
        <div className="integration-panel-body">
          <TabContent activeTab={tab}>
            {tab === 'interactions' && <InteractionContainer driverId={driverId} interactionsType="driver" />}
            {tab === 'driverContext' && <DriverContextPanel driverId={driverId} />}
            {tab === 'tasks' && <DriverTasksTab driverId={driverId} isSidebar />}
            {tab === 'conversations' && <ConversationsTab driverId={driverId} />}
          </TabContent>
        </div>
      </div>

      {/* Overlay for background click */}
      {/* {isPanelOpen && <div className="panel-overlay" onClick={() => setIsPanelOpen(false)} />} */}
    </div>
  )
}

const Hubspot = ({ driverId }) => {
  const [openSection, setOpenSection] = useState('')
  const { data: hubspotData, loading } = useQuery(HUBSPOT_INTEGRATION_DATA, {
    skip: !driverId,
    variables: { driverId, limit: 100 },
  })

  const hubspotTickets = hubspotData?.hubspotIntegrationData?.tickets || []
  const hubspotContactUrl = hubspotData?.hubspotIntegrationData?.hubspotContactUrl
  const newTicketsCount = hubspotData?.hubspotIntegrationData?.newTicketsCount || 0

  if (loading) return <Loader />

  if (!hubspotContactUrl) {
    return <p>Driver's BOS is not yet syned with Hubspot</p>
  }

  return (
    <div className="hubspot-container">
      <div className="hubspot-header">
        {hubspotContactUrl && (
          <a
            href={hubspotContactUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hubspot-link"
          >
            <i className="fa fa-external-link color-blue"></i> &nbsp;VIEW IN HUBSPOT
          </a>
        )}
      </div>

      <ul className="collapse-ul">
        <li className="collapse-li">
          <button
            className={`collapse-button ${openSection === 'tickets' ? 'open' : ''}`}
            type="button"
            onClick={() =>
              setOpenSection(openSection === 'tickets' ? '' : 'tickets')
            }
          >
            <span className="collapse-button-label">
              <i className="fa fa-ticket collapse-leading-icon" aria-hidden="true" />
              Tickets
            </span>
            <span className="collapse-button-meta">
              {newTicketsCount > 0 && (
                <Badge color="primary" className="new-badge">
                  {newTicketsCount} New
                </Badge>
              )}
              <i
                className={`collapse-arrow fa ${
                  openSection === 'tickets' ? 'fa-chevron-up' : 'fa-chevron-down'
                }`}
                aria-hidden="true"
              />
            </span>
          </button>
          <Collapse isOpen={openSection === 'tickets'}>
            <Card className="hubspot-card driver-context-card">
              <CardBody>
                {hubspotTickets.length === 0 ? (
                  <p className="empty-text">No tickets found.</p>
                ) : (
                  hubspotTickets.map(ticket => (
                    <div key={ticket.id} className="ticket-item">
                      <div className="ticket-icon">
                        <i className="fa fa-ticket" aria-hidden="true" />
                      </div>
                      <div className="ticket-content">
                        <div className="ticket-header">
                          <h6>{ticket.title}</h6>
                          <Badge
                            color={
                              ticket.status?.toLowerCase() === 'open'
                                ? 'success'
                                : 'secondary'
                            }
                          >
                            {ticket.status}
                          </Badge>
                        </div>
                        <p className="ticket-desc">{ticket.description}</p>
                        <div className="ticket-meta">
                          <span>
                            <i className="fa fa-clock-o" aria-hidden="true" />{' '}
                            {new Date(ticket.dateCreated).toLocaleDateString()}
                          </span>
                          <span className="ticket-id">#{ticket.id}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </Collapse>
        </li>
      </ul>
    </div>
  )
}

export default ExternalIntegrations
